import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SchoolLunchSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/forms/school-lunch-application/submissions');
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPDF = async (submissionId) => {
    let pdfUrl = null;
    try {
      const response = await axios.post('/api/forms/school-lunch-application/generate-pdf', {
        submissionId: submissionId
      }, {
        responseType: 'blob'
      });
      pdfUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(pdfUrl, '_blank');

      // Revoke the blob URL after a short delay to prevent memory leaks
      // Delay ensures the new tab has time to load the PDF before revocation
      setTimeout(() => {
        if (pdfUrl) {
          window.URL.revokeObjectURL(pdfUrl);
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Failed to generate PDF: ' + (error.response?.data?.error || error.message));
    } finally {
      // Additional cleanup in case of any issues
      if (pdfUrl) {
        setTimeout(() => {
          try {
            window.URL.revokeObjectURL(pdfUrl);
          } catch (e) {
            // Ignore errors if URL was already revoked
          }
        }, 2000);
      }
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this school lunch application? This submission will be removed from view and can be restored for 7 days before permanent deletion.')) {
      return;
    }

    try {
      await axios.delete(`/api/forms/school-lunch-application/submissions/${submissionId}`);
      // Reload submissions
      await loadSubmissions();
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Failed to delete submission: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStudentNames = (submission) => {
    try {
      // First try to get students from the database (students array)
      if (submission.students && Array.isArray(submission.students) && submission.students.length > 0) {
        const names = submission.students.map(student =>
          `${student.first_name} ${student.last_name}`
        );
        return names.join(', ');
      }

      // Fallback to students listed in the form submission data
      const formData = submission.submission_data;
      if (formData && formData.children && Array.isArray(formData.children)) {
        const names = formData.children.map(child =>
          `${child.first_name} ${child.last_name}`
        );
        return names.join(', ');
      }

      return 'No students found';
    } catch (error) {
      return 'Error loading students';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>School Lunch Applications</h2>
        <div>Loading submissions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>School Lunch Applications</h2>
        <div style={{ color: 'red' }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>School Lunch Applications</h2>
      <p>View submitted school lunch applications from parents in your district.</p>

      {submissions.length === 0 ? (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          background: '#f8f9fa',
          borderRadius: '8px',
          marginTop: '2rem'
        }}>
          <h3>No submissions yet</h3>
          <p>When parents submit school lunch applications, they will appear here.</p>
        </div>
      ) : (
        <div style={{ marginTop: '2rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
            gap: '1rem',
            fontWeight: 'bold',
            padding: '1rem',
            background: '#f8f9fa',
            borderRadius: '8px 8px 0 0',
            border: '1px solid #dee2e6',
            borderBottom: 'none'
          }}>
            <div>Parent Name</div>
            <div>Students</div>
            <div>Email</div>
            <div>Date Submitted</div>
            <div>View PDF</div>
            <div>Delete</div>
          </div>

          {submissions.map((submission, index) => (
            <div
              key={submission.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
                gap: '1rem',
                padding: '1rem',
                border: '1px solid #dee2e6',
                borderTop: index === 0 ? 'none' : '1px solid #dee2e6',
                background: 'white'
              }}
            >
              <div>
                {submission.parent_first_name} {submission.parent_last_name}
              </div>
              <div style={{ fontSize: '0.9em', color: '#666' }}>
                {getStudentNames(submission)}
              </div>
              <div>{submission.parent_email}</div>
              <div>
                {new Date(submission.submitted_at).toLocaleDateString()}
              </div>
              <div>
                <button
                  onClick={() => handleViewPDF(submission.id)}
                  style={{
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8em'
                  }}
                >
                  View PDF
                </button>
              </div>
              <div>
                <button
                  onClick={() => handleDelete(submission.id)}
                  style={{
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8em'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SchoolLunchSubmissions;
